import { useInquiryModal } from '@/components/inquiry/InquiryModal';
import { AuthorHero } from '@/components/journal/author-hero';
import { BreadcrumbTrail, type BreadcrumbCrumb } from '@/components/journal/breadcrumb-trail';
import { ShareBar } from '@/components/journal/share-bar';
import MetaHead from '@/components/meta-head';
import { ResponsiveImage } from '@/components/ResponsiveImage';
import PublicLayout from '@/layouts/public-layout';
import { focalPointToObjectPosition, resolveEffectiveFocalPoint } from '@/lib/focal-point';
import { variantUrlFromString } from '@/lib/responsive-image';
import { tiptapToEditorialHtml } from '@/lib/tiptap-to-editorial-html';
import type { ResponsiveImageMedia } from '@/types/responsive-image';
import { AuthorByline } from '@/pages/static/journal/AuthorByline';
import JournalNav from '@/pages/static/journal/JournalNav';
import { Link } from '@inertiajs/react';
import DOMPurify from 'isomorphic-dompurify';
import { ArrowLeft, ArrowRight, Camera, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ArticleProps {
    article: {
        id: number;
        title: string;
        slug: string;
        excerpt: string | null;
        content: Record<string, unknown> | null;
        main_image: string | null;
        main_image_credit: string | null;
        main_image_credit_url: string | null;
        main_image_caption: string | null;
        main_image_focal_point: { x: number; y: number } | null;
        main_image_focal_point_shared: { x: number; y: number } | null;
        main_image_variants: number[];
        body_media_variants: Record<string, number[]>;
        kicker: string | null;
        published_at: string | null;
        content_modified_at?: string | null;
        seo_title: string | null;
        seo_description: string | null;
        canonical_url: string | null;
    };
    category: {
        name: string;
        slug: string;
    };
    author: {
        name: string;
        avatar: string | null;
        bio: string | null;
        profile_url: string | null;
        is_agent: boolean;
        agent_id: number | null;
        agent_slug: string | null;
        /** r77 E-E-A-T: "Elite Travel Advisor" / "Travel Advisor" for agent bylines (matches the Person schema). */
        job_title?: string | null;
        /** r77 E-E-A-T: tenure from agent_since — null when not held, never guessed. */
        years_as_agent?: number | null;
    } | null;
    // Interview lead routing: when set, this advisor (not the author) receives the
    // article's leads and is featured in the CTA. The byline still shows the author.
    interview_agent: {
        agent_id: number;
        name: string;
        first_name: string | null;
        avatar: string | null;
        slug: string | null;
        profile_url: string | null;
        bio: string | null;
    } | null;
    // Server-resolved agent id both the CTA and the first_touch cascade credit
    // (interview subject when flagged, else the author's agent). Drives inquiry context.
    inquiry_agent_id: number | null;
    related: {
        id: number;
        title: string;
        slug: string;
        excerpt: string | null;
        main_image: string | null;
        author_name: string | null;
        published_at: string | null;
    }[];
    sections: { name: string; tag: string }[];
    nextArticle: { title: string; slug: string } | null;
    // Home › Journal › {Category} › {Article title}. Server-built (ArticleController::show);
    // the JSON-LD BreadcrumbList is generated from the SAME array. Optional until the
    // server side lands — render nothing when absent.
    breadcrumbs?: BreadcrumbCrumb[];
    isPreview?: boolean;
    previewMeta?: {
        status: string;
        editUrl: string;
    };
}

function formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

interface LightboxImage {
    src: string;
    alt: string;
    caption?: string;
    credit?: string;
}

function GalleryLightbox({ images, startIndex, onClose }: { images: LightboxImage[]; startIndex: number; onClose: () => void }) {
    const [index, setIndex] = useState(startIndex);
    const img = images[index];

    const prev = useCallback(() => setIndex((i) => (i > 0 ? i - 1 : images.length - 1)), [images.length]);
    const next = useCallback(() => setIndex((i) => (i < images.length - 1 ? i + 1 : 0)), [images.length]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') prev();
            if (e.key === 'ArrowRight') next();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose, prev, next]);

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
            style={{ backgroundColor: 'rgba(11, 34, 34, 0.95)' }}
            onClick={onClose}
        >
            <div className="relative flex max-h-[85vh] max-w-[88vw] flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img src={img.src} alt={img.alt} className="max-h-[70vh] max-w-full object-contain" />
                <div className="mt-4 text-center">
                    {img.credit && (
                        <span className="text-[11px] tracking-[0.15em] uppercase" style={{ color: 'rgba(245, 245, 245, 0.4)' }}>
                            {img.credit}
                        </span>
                    )}
                    {img.caption && (
                        <p className="mt-1 text-sm italic" style={{ fontFamily: "'Didot', Georgia, serif", color: 'rgba(245, 245, 245, 0.65)' }}>
                            {img.caption}
                        </p>
                    )}
                    {images.length > 1 && (
                        <div className="mt-2 text-[11px] tracking-[0.2em] uppercase" style={{ color: 'rgba(245, 245, 245, 0.3)' }}>
                            {index + 1} / {images.length}
                        </div>
                    )}
                </div>
            </div>

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                className="absolute top-6 right-6 cursor-pointer transition-opacity hover:opacity-100"
                style={{ color: 'rgba(245, 245, 245, 0.5)' }}
            >
                <X size={24} />
            </button>
            {images.length > 1 && (
                <>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            prev();
                        }}
                        className="absolute top-1/2 left-4 -translate-y-1/2 cursor-pointer p-3 transition-opacity hover:opacity-100"
                        style={{ color: 'rgba(245, 245, 245, 0.5)' }}
                    >
                        <ChevronLeft size={32} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            next();
                        }}
                        className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer p-3 transition-opacity hover:opacity-100"
                        style={{ color: 'rgba(245, 245, 245, 0.5)' }}
                    >
                        <ChevronRight size={32} />
                    </button>
                </>
            )}
        </div>,
        document.body,
    );
}

export default function ArticleShow(props: ArticleProps) {
    const { article, isPreview } = props;
    const metaTitle = article.seo_title ?? `${article.title} | LuxRally Journal`;
    const metaDescription = article.seo_description ?? article.excerpt ?? undefined;

    return (
        <PublicLayout>
            <MetaHead
                title={isPreview ? `Preview: ${article.title}` : metaTitle}
                description={metaDescription}
                canonical={article.canonical_url ?? undefined}
                image={article.main_image ?? undefined}
                noIndex={isPreview}
            />
            <ArticleContent {...props} />
        </PublicLayout>
    );
}

/**
 * Inner component rendered inside PublicLayout so useInquiryModal()
 * can access the InquiryModalProvider context.
 */
function ArticleContent({
    article,
    category,
    author,
    interview_agent,
    inquiry_agent_id,
    related,
    sections,
    nextArticle,
    breadcrumbs,
    isPreview,
    previewMeta,
}: ArticleProps) {
    const contentHtml = article.content ? tiptapToEditorialHtml(article.content, article.body_media_variants ?? {}) : null;
    const proseRef = useRef<HTMLDivElement>(null);
    const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; startIndex: number } | null>(null);
    const { openInquiry } = useInquiryModal();

    // r77 E-E-A-T (visible, not just schema): "Updated {date}" only when the
    // content genuinely moved after publication (content_modified_at is bumped
    // by ArticleContentObserver on real title/body/hero changes, same source
    // as schema dateModified) and on a different calendar day; a one-line
    // credentials fragment only from data we actually hold (agent_since).
    const updatedLabel = useMemo(() => {
        if (!article.published_at || !article.content_modified_at) return null;
        const pub = new Date(article.published_at);
        const mod = new Date(article.content_modified_at);
        if (Number.isNaN(pub.getTime()) || Number.isNaN(mod.getTime()) || mod <= pub) return null;
        if (pub.toDateString() === mod.toDateString()) return null;
        return formatDate(article.content_modified_at);
    }, [article.published_at, article.content_modified_at]);
    const authorCredentials = useMemo(() => {
        const years = author?.years_as_agent ?? null;
        if (years == null || years < 1) return null;
        return `${years} ${years === 1 ? 'year' : 'years'} advising LuxRally clients`;
    }, [author?.years_as_agent]);

    // Who an inquiry from this article is credited to (for display): the interview
    // subject when flagged, else the author if they're an agent. The authoritative
    // agent id is the server-resolved inquiry_agent_id (matches the first_touch
    // cascade exactly). agentContext spreads into every openInquiry() call.
    const agentContext = useMemo(() => {
        const display = interview_agent
            ? { name: interview_agent.name, first_name: interview_agent.first_name, avatar: interview_agent.avatar, slug: interview_agent.slug }
            : author?.is_agent
              ? { name: author.name, first_name: author.name.split(' ')[0], avatar: author.avatar, slug: author.agent_slug }
              : null;
        if (!inquiry_agent_id) return {};
        return {
            agent_id: inquiry_agent_id,
            ...(display
                ? {
                      agent_name: display.first_name || display.name.split(' ')[0],
                      agent_photo: display.avatar || undefined,
                      agent_slug: display.slug || undefined,
                  }
                : {}),
        };
    }, [inquiry_agent_id, interview_agent, author]);

    useEffect(() => {
        const container = proseRef.current;
        if (!container) return;

        const handleClick = (e: MouseEvent) => {
            // Handle in-content CTA inquiry trigger
            const ctaBtn = (e.target as HTMLElement).closest('[data-inquiry-trigger]');
            if (ctaBtn) {
                e.preventDefault();
                openInquiry({
                    source: 'journal_article_cta',
                    article_id: article.id,
                    article_slug: article.slug,
                    page_url: window.location.href,
                    ...agentContext,
                });
                return;
            }

            // Handle gallery lightbox
            const img = (e.target as HTMLElement).closest('img[data-gallery]') as HTMLImageElement | null;
            if (!img) return;

            const gallery = img.closest('.editorial-gallery');
            if (!gallery) return;

            const galleryImgs = Array.from(gallery.querySelectorAll('img[data-gallery]'));
            const images: LightboxImage[] = galleryImgs.map((el) => {
                const figure = el.closest('figure');
                return {
                    src: (el as HTMLImageElement).src,
                    alt: (el as HTMLImageElement).alt,
                    caption: figure?.querySelector('.image-caption')?.textContent || undefined,
                    credit: figure?.querySelector('.photo-credit')?.textContent?.trim() || undefined,
                };
            });
            const startIndex = galleryImgs.indexOf(img);
            setLightbox({ images, startIndex: Math.max(0, startIndex) });
        };

        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    }, [contentHtml, openInquiry, article, agentContext]);

    return (
        <>
            {isPreview && previewMeta && (
                <div className="bg-[#0b2222] text-[#F5F5F5]">
                    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center rounded-full border border-[#F5F5F5]/20 bg-[#F5F5F5]/10 px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-[#F5F5F5]/80">
                                PREVIEW
                            </span>
                            <span className="text-xs text-[#F5F5F5]/40">
                                {previewMeta.status !== 'published' ? 'Not yet published' : 'Published'}
                            </span>
                        </div>
                        <Link
                            href={previewMeta.editUrl}
                            className="inline-flex items-center gap-1.5 rounded-sm border border-[#F5F5F5]/20 bg-[#F5F5F5]/5 px-3 py-1 text-xs text-[#F5F5F5]/70 transition-colors hover:bg-[#F5F5F5]/10 hover:text-[#F5F5F5]"
                        >
                            Back to Editor
                        </Link>
                    </div>
                </div>
            )}

            <div style={{ backgroundColor: '#fdfaf3', minHeight: '100vh' }}>
                <JournalNav
                    sections={sections}
                    activeTag={category.slug}
                    sticky={false}
                    leftAction={
                        <button
                            type="button"
                            onClick={() => {
                                if (typeof window !== 'undefined' && window.history.length > 1) {
                                    window.history.back();
                                } else {
                                    window.location.href = '/journal';
                                }
                            }}
                            aria-label="Back to previous page"
                            className="inline-flex h-8 w-8 items-center justify-center"
                        >
                            <ArrowLeft size={16} strokeWidth={1.5} />
                        </button>
                    }
                    rightAction={
                        nextArticle ? (
                            <Link
                                href={`/journal/${nextArticle.slug}`}
                                aria-label={`Next article: ${nextArticle.title}`}
                                className="inline-flex h-8 w-8 items-center justify-center"
                            >
                                <ArrowRight size={16} strokeWidth={1.5} />
                            </Link>
                        ) : null
                    }
                />
                <article>
                    {/* Hero image — cinematic first impression */}
                    {article.main_image && (
                        <div className="relative">
                            <ResponsiveImage
                                /* Variant widths are server-confirmed (article.main_image_variants
                                   from ArticleController::variantsForCdnUrl). Only widths that
                                   actually exist on S3 are emitted in srcset — prevents the gray
                                   hero bug where a 403'd <source> URL leaves the picture blank. */
                                media={article.main_image.startsWith('https://cdnweb.luxrallytravel.com/') && (article.main_image_variants?.length ?? 0) > 0
                                    ? ({
                                          url: article.main_image,
                                          responsive_variants: {
                                              avif: Object.fromEntries(
                                                  (article.main_image_variants ?? []).map((w) => [w, variantUrlFromString(article.main_image!, w, 'avif')!]),
                                              ),
                                              webp: Object.fromEntries(
                                                  (article.main_image_variants ?? []).map((w) => [w, variantUrlFromString(article.main_image!, w, 'webp')!]),
                                              ),
                                          },
                                      } satisfies ResponsiveImageMedia)
                                    : undefined}
                                src={article.main_image}
                                alt={article.title}
                                sizes="100vw"
                                loading="eager"
                                fetchPriority="high"
                                className="w-full object-cover"
                                style={{
                                    aspectRatio: '3 / 2',
                                    maxHeight: '75vh',
                                    objectPosition: focalPointToObjectPosition(
                                        resolveEffectiveFocalPoint(article.main_image_focal_point, article.main_image_focal_point_shared),
                                    ),
                                }}
                            />
                            {/* Subtle vignette at bottom */}
                            <div
                                className="pointer-events-none absolute inset-0"
                                style={{ background: 'linear-gradient(to top, rgba(11, 34, 34, 0.25) 0%, transparent 40%)' }}
                            />
                            {/* Image credit — bottom right, sat on a subtle glass pill for legibility on any background. */}
                            {article.main_image_credit && (
                                <div className="absolute right-4 bottom-3">
                                    {article.main_image_credit_url ? (
                                        <a
                                            href={article.main_image_credit_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Photo by ${article.main_image_credit}`}
                                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.15em] backdrop-blur-sm transition-colors"
                                            style={{ backgroundColor: 'rgba(101, 62, 32, 0.4)', color: 'rgba(245, 245, 245, 0.92)' }}
                                        >
                                            <Camera size={10} style={{ stroke: 'rgba(245, 245, 245, 0.9)' }} />
                                            {article.main_image_credit}
                                        </a>
                                    ) : (
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.15em] backdrop-blur-sm"
                                            style={{ backgroundColor: 'rgba(101, 62, 32, 0.4)', color: 'rgba(245, 245, 245, 0.92)' }}
                                        >
                                            <Camera size={10} style={{ stroke: 'rgba(245, 245, 245, 0.9)' }} />
                                            {article.main_image_credit}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Masthead — breadcrumb, kicker, title */}
                    <header className="mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
                        {breadcrumbs && breadcrumbs.length > 0 && <BreadcrumbTrail crumbs={breadcrumbs} className="mb-6" />}
                        {article.kicker && (
                            <p className="mb-5 text-[11px] font-medium tracking-[0.35em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.45)' }}>
                                {article.kicker}
                            </p>
                        )}
                        <h1
                            className="mb-6 text-4xl leading-tight font-normal md:text-5xl lg:text-[3.5rem]"
                            style={{ fontFamily: "'Didot', 'Playfair Display', Georgia, serif", color: '#0e2727' }}
                        >
                            {article.title}
                        </h1>
                        {article.excerpt && (
                            <p
                                data-speakable="summary"
                                className="mx-auto max-w-2xl text-base leading-relaxed italic md:text-lg"
                                style={{
                                    fontFamily: "'Didot', 'Playfair Display', Georgia, serif",
                                    color: 'rgba(14, 39, 39, 0.6)',
                                }}
                            >
                                {article.excerpt}
                            </p>
                        )}

                        {/* Author + Date */}
                        <div className="mt-8 flex flex-col items-center gap-2">
                            {author?.name && (
                                <p className="text-[11px] font-medium tracking-[0.25em] uppercase" style={{ color: '#0b2222' }}>
                                    By{' '}
                                    {author.profile_url ? (
                                        <Link
                                            href={author.profile_url}
                                            className="underline-offset-4 transition-opacity hover:underline hover:opacity-80"
                                        >
                                            {author.name}
                                        </Link>
                                    ) : (
                                        author.name
                                    )}
                                </p>
                            )}
                            {article.published_at && (
                                <p className="text-[11px] tracking-[0.2em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.45)' }}>
                                    {formatDate(article.published_at)}
                                    {/* r77 E-E-A-T: a visible "Updated" date whenever the content moved after publication (same signal as schema dateModified). */}
                                    {updatedLabel && (
                                        <>
                                            <span aria-hidden="true"> · </span>
                                            <time dateTime={article.content_modified_at ?? undefined} data-testid="article-updated">
                                                Updated {updatedLabel}
                                            </time>
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    </header>

                    {/* Caption below masthead if present */}
                    {article.main_image_caption && (
                        <div className="mx-auto -mt-4 mb-4 max-w-3xl px-6">
                            <p className="text-center text-[11px] tracking-[0.2em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.35)' }}>
                                {article.main_image_caption}
                            </p>
                        </div>
                    )}

                    {/* Article body */}
                    <div className="mx-auto max-w-3xl px-6 pb-8">
                        {contentHtml ? (
                            <div
                                ref={proseRef}
                                className="editorial-prose"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(contentHtml, {
                                        ADD_TAGS: ['svg', 'path', 'circle', 'iframe', 'picture', 'source'],
                                        ADD_ATTR: [
                                            'viewBox',
                                            'fill',
                                            'stroke',
                                            'stroke-width',
                                            'stroke-linecap',
                                            'stroke-linejoin',
                                            'cx',
                                            'cy',
                                            'r',
                                            'd',
                                            'xmlns',
                                            'allow',
                                            'allowfullscreen',
                                            'frameborder',
                                            'data-gallery',
                                            'data-count',
                                            'data-inquiry-trigger',
                                            'srcset',
                                            'sizes',
                                            'type',
                                        ],
                                        ALLOWED_URI_REGEXP:
                                            /^(?:(?:https?|mailto|tel|ftp|data|blob|youtube|vimeo):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                                    }),
                                }}
                            />
                        ) : (
                            <p className="text-center italic" style={{ color: 'rgba(14, 39, 39, 0.5)' }}>
                                No content yet.
                            </p>
                        )}
                    </div>

                    <ShareBar title={article.title} image={article.main_image ?? undefined} className="pb-12" />

                    {interview_agent ? (
                        // Interview: the CTA features the subject (who receives leads), but
                        // "Read more from …" credits the writer (the author).
                        <AuthorHero
                            author={{
                                name: interview_agent.name,
                                avatar: interview_agent.avatar,
                                bio: interview_agent.bio,
                                profile_url: interview_agent.profile_url,
                                is_agent: true,
                                agent_id: interview_agent.agent_id,
                                agent_slug: interview_agent.slug,
                            }}
                            articleId={article.id}
                            articleSlug={article.slug}
                            kicker="Plan your journey with"
                            linkToExternalProfile
                            readMoreOverride={author ? { name: author.name, href: author.profile_url } : undefined}
                        />
                    ) : author?.is_agent ? (
                        <AuthorHero author={author} articleId={article.id} articleSlug={article.slug} />
                    ) : (
                        <>
                            {/* Post-article CTA — high-intent conversion point */}
                            <div className="mx-auto max-w-3xl px-6">
                                <div className="border-t py-12 text-center" style={{ borderColor: 'rgba(14, 39, 39, 0.1)' }}>
                                    <p
                                        className="text-xl font-normal italic md:text-2xl"
                                        style={{ fontFamily: "'Didot', 'Playfair Display', Georgia, serif", color: '#0e2727' }}
                                    >
                                        Ready to plan your journey?
                                    </p>
                                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed" style={{ color: 'rgba(14, 39, 39, 0.55)' }}>
                                        Our advisors have walked every lodge, driven every route, and planned every detail — so you don't have to.
                                    </p>
                                    <button
                                        onClick={() =>
                                            openInquiry({
                                                source: 'journal_article_bottom',
                                                article_id: article.id,
                                                article_slug: article.slug,
                                                page_url: window.location.href,
                                            })
                                        }
                                        className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-sm border px-6 py-2 text-[11px] font-medium tracking-[0.25em] uppercase transition-colors"
                                        style={{
                                            borderColor: '#0b2222',
                                            backgroundColor: '#0b2222',
                                            color: '#F5F5F5',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#0e2727';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#0b2222';
                                        }}
                                    >
                                        Speak with an Advisor
                                        <span aria-hidden="true">&rarr;</span>
                                    </button>
                                </div>
                            </div>

                            {/* Author bio section — centered, editorial */}
                            {author && (
                                <div className="mx-auto max-w-3xl px-6 py-12">
                                    <div className="flex flex-col items-center text-center">
                                        {author.avatar && (
                                            <img src={author.avatar} alt={author.name} className="mb-4 h-20 w-20 rounded-full object-cover" />
                                        )}
                                        <p
                                            className="mb-2 text-[11px] font-medium tracking-[0.3em] uppercase"
                                            style={{ color: 'rgba(14, 39, 39, 0.4)' }}
                                        >
                                            Written by
                                        </p>
                                        {author.profile_url ? (
                                            <Link
                                                href={author.profile_url}
                                                className="text-lg font-normal transition-colors hover:text-[#0b2222]/70 md:text-xl"
                                                style={{
                                                    fontFamily: "'Didot', 'Playfair Display', Georgia, serif",
                                                    color: '#0e2727',
                                                }}
                                            >
                                                {author.name}
                                            </Link>
                                        ) : (
                                            <h3
                                                className="text-lg font-normal md:text-xl"
                                                style={{
                                                    fontFamily: "'Didot', 'Playfair Display', Georgia, serif",
                                                    color: '#0e2727',
                                                }}
                                            >
                                                {author.name}
                                            </h3>
                                        )}
                                        {/* r77 E-E-A-T: role + honest credentials line (tenure only when agent_since is held), then the dates. */}
                                        {(author.job_title || authorCredentials) && (
                                            <p
                                                className="mt-1 text-[11px] font-medium tracking-[0.2em] uppercase"
                                                style={{ color: 'rgba(14, 39, 39, 0.55)' }}
                                                data-testid="author-credentials"
                                            >
                                                {author.job_title}
                                                {author.job_title && authorCredentials ? <span aria-hidden="true"> · </span> : null}
                                                {authorCredentials}
                                            </p>
                                        )}
                                        {author.bio && (
                                            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed" style={{ color: 'rgba(14, 39, 39, 0.55)' }}>
                                                {author.bio}
                                            </p>
                                        )}
                                        {article.published_at && (
                                            <p className="mt-3 text-[11px] tracking-[0.2em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.4)' }}>
                                                Published {formatDate(article.published_at)}
                                                {updatedLabel && (
                                                    <>
                                                        <span aria-hidden="true"> · </span>
                                                        <time dateTime={article.content_modified_at ?? undefined}>Updated {updatedLabel}</time>
                                                    </>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Related articles */}
                    {related.length > 0 ? (
                        <div className="mx-auto max-w-5xl px-6 pt-6 pb-16">
                            <div className="mb-10 flex items-center gap-6">
                                <span className="h-px flex-1" style={{ backgroundColor: 'rgba(14, 39, 39, 0.1)' }} />
                                <h3
                                    className="text-[11px] font-medium tracking-[0.3em] whitespace-nowrap uppercase"
                                    style={{ color: 'rgba(14, 39, 39, 0.45)' }}
                                >
                                    Related Stories
                                </h3>
                                <span className="h-px flex-1" style={{ backgroundColor: 'rgba(14, 39, 39, 0.1)' }} />
                            </div>
                            <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3">
                                {related.map((item) => (
                                    <Link key={item.id} href={`/journal/${item.slug}`} className="group">
                                        {item.main_image && (
                                            <div className="aspect-[4/3] overflow-hidden">
                                                <img
                                                    src={item.main_image}
                                                    alt={item.title}
                                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className="mt-4">
                                            <h4
                                                className="line-clamp-2 text-lg font-normal transition-colors group-hover:text-[#0b2222]/70"
                                                style={{
                                                    fontFamily: "'Didot', 'Playfair Display', Georgia, serif",
                                                    color: '#0e2727',
                                                }}
                                            >
                                                {item.title}
                                            </h4>
                                            {item.excerpt && (
                                                <p className="mt-2 line-clamp-2 text-sm" style={{ color: 'rgba(14, 39, 39, 0.6)' }}>
                                                    {item.excerpt}
                                                </p>
                                            )}
                                            {item.author_name && <AuthorByline name={item.author_name} className="mt-3 text-[#0e2727]/55" />}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="mx-auto max-w-3xl border-t px-6 py-12 text-center" style={{ borderColor: 'rgba(14, 39, 39, 0.1)' }}>
                            <Link
                                href={`/journal/category/${category.slug}`}
                                className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase transition-colors hover:text-[#0b2222]"
                                style={{ color: 'rgba(14, 39, 39, 0.45)' }}
                            >
                                Explore more in {category.name}
                                <span aria-hidden="true">&rarr;</span>
                            </Link>
                        </div>
                    )}
                </article>
            </div>

            {lightbox && <GalleryLightbox images={lightbox.images} startIndex={lightbox.startIndex} onClose={() => setLightbox(null)} />}
        </>
    );
}
